import React, { useCallback, useEffect, useState } from 'react'

const getValueFromLocalStorage = (key: string) => {
  if (typeof localStorage === 'undefined') {
    return null
  }

  const value = localStorage.getItem(key) ?? 'null'

  try {
    return JSON.parse(value)
  } catch (error) {
    console.log('Error parsing local storage value', error)
  }

  return null
}

const saveValueToLocalStorage = <T>(key: string, value: T) => {
  if (typeof localStorage === 'undefined') {
    return null
  }

  if (value === undefined) {
    return localStorage.removeItem(key)
  }

  return localStorage.setItem(key, JSON.stringify(value))
}

export const useLocalStorage = <T extends any>(
  storageKey: string,
  initialValue?: T | (() => T)
): [T | undefined, React.Dispatch<React.SetStateAction<T | undefined>>, () => void] => {
  const [state, setState] = useState<T | undefined>(() => {
    const storedValue = getValueFromLocalStorage(storageKey)

    if (storedValue !== null) {
      return storedValue
    }

    if (typeof initialValue === 'function') {
      return (initialValue as () => T)()
    }

    return initialValue
  })

  const removeKey = useCallback(() => {
    localStorage.removeItem(storageKey)
  }, [storageKey])

  useEffect(() => {
    saveValueToLocalStorage(storageKey, state)
  }, [storageKey, state])

  return [state, setState, removeKey]
}
